// Quick Wins System - Contextual suggestions after memory saves
// Provides immediate value and guides users to next steps

export interface QuickWin {
  id: string
  type: "tip" | "action" | "insight" | "next_memory"
  title: string
  description: string
  priority: "high" | "medium" | "low"
  relatedSection?: string
  actionLabel?: string
  actionUrl?: string
}

// Section-based quick wins suggestions
const SECTION_QUICK_WINS: Record<string, QuickWin[]> = {
  identity: [
    {
      id: "identity-1",
      type: "next_memory",
      title: "Add your mission statement",
      description: "A clear mission helps BizzIQ understand your core purpose and provide better strategic advice.",
      priority: "high",
      relatedSection: "mission-vision",
      actionLabel: "Add Mission",
    },
    {
      id: "identity-2",
      type: "tip",
      title: "Industry context unlocked",
      description: "Now that I know your industry, I can provide specialized insights and benchmarks.",
      priority: "medium",
    },
  ],
  "mission-vision": [
    {
      id: "mv-1",
      type: "insight",
      title: "Mission alignment check",
      description: "I can now evaluate your strategies against your stated mission and vision.",
      priority: "high",
    },
    {
      id: "mv-2",
      type: "next_memory",
      title: "Define your target audience",
      description: "Understanding who you serve helps tailor your mission into actionable strategies.",
      priority: "high",
      relatedSection: "audience",
      actionLabel: "Define Audience",
    },
  ],
  offerings: [
    {
      id: "off-1",
      type: "action",
      title: "Analyze your pricing",
      description: "With your offerings defined, I can help analyze pricing strategy and market positioning.",
      priority: "high",
      actionLabel: "Analyze Pricing",
    },
    {
      id: "off-2",
      type: "next_memory",
      title: "Add competitor information",
      description: "Understanding your competition helps me provide better differentiation strategies.",
      priority: "medium",
      relatedSection: "competition",
      actionLabel: "Add Competitors",
    },
  ],
  audience: [
    {
      id: "aud-1",
      type: "insight",
      title: "Persona unlocked",
      description: "I can now tailor marketing and sales recommendations to your specific audience.",
      priority: "high",
    },
    {
      id: "aud-2",
      type: "action",
      title: "Generate customer insights",
      description: "Let me analyze your audience data and suggest engagement strategies.",
      priority: "medium",
      actionLabel: "Generate Insights",
    },
  ],
  finances: [
    {
      id: "fin-1",
      type: "insight",
      title: "Financial health check available",
      description: "I can now provide revenue analysis and budget recommendations.",
      priority: "high",
    },
    {
      id: "fin-2",
      type: "next_memory",
      title: "Add your goals",
      description: "Connect your financials to your goals for strategic planning.",
      priority: "high",
      relatedSection: "goals",
      actionLabel: "Set Goals",
    },
  ],
  goals: [
    {
      id: "goal-1",
      type: "action",
      title: "Create action plan",
      description: "With your goals defined, I can help break them into actionable milestones.",
      priority: "high",
      actionLabel: "Create Plan",
    },
    {
      id: "goal-2",
      type: "insight",
      title: "Goal tracking enabled",
      description: "I'll now factor your goals into all strategic recommendations.",
      priority: "medium",
    },
  ],
}

// Field-specific quick wins
const FIELD_QUICK_WINS: Record<string, QuickWin[]> = {
  company_name: [
    {
      id: "name-1",
      type: "tip",
      title: "Great start!",
      description: "I'll remember your company name and use it in all our conversations.",
      priority: "low",
    },
  ],
  revenue: [
    {
      id: "rev-1",
      type: "insight",
      title: "Revenue context acquired",
      description: "I can now provide size-appropriate recommendations and industry benchmarks.",
      priority: "high",
    },
  ],
  employee_count: [
    {
      id: "emp-1",
      type: "tip",
      title: "Team size noted",
      description: "I'll factor your team capacity into operational and hiring recommendations.",
      priority: "medium",
    },
  ],
}

/**
 * Get quick wins based on the section and field that was just saved
 */
export function getQuickWins(sectionSlug: string, fieldKey?: string, memoryCount?: number): QuickWin[] {
  const wins: QuickWin[] = []

  // Add field-specific wins first (higher priority)
  if (fieldKey && FIELD_QUICK_WINS[fieldKey]) {
    wins.push(...FIELD_QUICK_WINS[fieldKey])
  }

  // Add section wins
  if (SECTION_QUICK_WINS[sectionSlug]) {
    wins.push(...SECTION_QUICK_WINS[sectionSlug])
  }

  // Add milestone wins based on memory count
  if (memoryCount !== undefined) {
    if (memoryCount === 1) {
      wins.unshift({
        id: "milestone-1",
        type: "insight",
        title: "First memory saved!",
        description: "You're building your Business Brain. Each memory makes BizzIQ smarter about your business.",
        priority: "high",
      })
    } else if (memoryCount === 5) {
      wins.unshift({
        id: "milestone-5",
        type: "insight",
        title: "5 memories milestone",
        description: "You're making great progress. BizzIQ is learning your business.",
        priority: "medium",
      })
    } else if (memoryCount === 10) {
      wins.unshift({
        id: "milestone-10",
        type: "insight",
        title: "10 memories - Dashboard unlocking soon!",
        description: "Just 2 more memories to unlock your first dashboard with personalized insights.",
        priority: "high",
      })
    } else if (memoryCount === 12) {
      wins.unshift({
        id: "milestone-12",
        type: "action",
        title: "Dashboard Unlocked!",
        description: "Your Owner Dashboard is now available with personalized business insights.",
        priority: "high",
        actionLabel: "View Dashboard",
        actionUrl: "/dashboards",
      })
    }
  }

  // Sort by priority and limit to top 3
  const priorityOrder = { high: 0, medium: 1, low: 2 }
  return wins.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]).slice(0, 3)
}

/**
 * Get a contextual tip based on current profile completion
 */
export function getContextualTip(
  completedSections: string[],
  totalSections: number,
): { tip: string; progress: number } {
  const progress = Math.round((completedSections.length / totalSections) * 100)

  if (progress < 25) {
    return {
      tip: "Keep going! Each piece of information helps me give you better advice.",
      progress,
    }
  } else if (progress < 50) {
    return {
      tip: "Great progress! You're building a solid foundation for strategic insights.",
      progress,
    }
  } else if (progress < 75) {
    return {
      tip: "Impressive! BizzIQ now has a strong understanding of your business.",
      progress,
    }
  } else if (progress < 100) {
    return {
      tip: "Almost there! A complete profile unlocks the most powerful insights.",
      progress,
    }
  } else {
    return {
      tip: "Excellent! Your Business Brain is fully built. Ask me anything about your business strategy.",
      progress,
    }
  }
}
